import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {useLocation} from 'react-router-dom'
import useEinstein from '../../../store/hooks/useEinstein'
import {Box, Flex, Stack, Text, Container, BaseButton} from '../../../vanilla'
import {Breadcrumbs} from '../../breadcrumbs'
import {flowResult} from 'mobx'
import {
  InstantSearch as AlgoliaInstantSearch,
  useInstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-hooks-web'
import {Hits} from './hits'
import {Stats} from './stats'
import {
  useBasketStore,
  useCategoryStore,
  useContentStore,
  useGlobalStore,
  useProductStore,
  useRetailMediaStore
} from '../../../store/hooks/useStore'
import {Filters} from './filters'
import {Pagination} from './pagination'
import algoliaRouting from '../routing'
import {Link} from '../../link'
import {PLPSkeletonTiles} from '../../skeletons'
import StructuredDataType, {
  buildBreadcrumbsStructuredData,
  buildProductListStructuredData,
} from '../../../utils/structured-data'
import {Seo} from '../../seo'
import {RefinementsDisplay} from './refinements-display'
import * as styles from './styles.css'
import {quickLinksContainer, searchSuggestionItem} from './styles.css'
import debounce from 'lodash.debounce'
import {useViewItemListTracking} from '../../../analytics/view-item-list-tracking'
import {useSelectPromotionTracking} from '../../../analytics/promotion-tracking'
import {Scrollbar} from '../../scrollbar/scrollbar'
import {isMobile, isServer} from '../../../utils/utils'
import {getRedirectURL} from '../../../utils/search-utils'
import useNavigation from '../../../hooks/use-navigation'
import {InstantSearchProps, ResultsHeaderProps, SearchSuggestionsProps} from '../../../types/algolia'
import {usePLPBanners} from '../../../store/hooks/usePLPBanners'
import {useSearchPageContent} from '../../../store/hooks/useSearchPageContent'
import {createCategoryNameString, getMaxSponsoredProducts} from '../../../utils/retail-media-utils'
import {useCustomerStore} from '../../../store/hooks/useStore'
import {useItemsPrices} from '../util/useItemPrice'
import {transformHits} from '../util/transformHit'

const InstantSearch = observer(
  ({
    initialUiState,
    category,
    children,
    contentAboveHeader,
    inGridPromos,
    serverState,
    serverUrl,
    h2,
    pageName,
    presentationAttributes,
    categoryTree,
    seo,
    rowSlots = []
  }: InstantSearchProps) => {
    const {search} = useLocation()
    const {algoliaIndexBase} = useGlobalStore()
    const searchIndex = `${algoliaIndexBase}__products__default`
    const routing = algoliaRouting(serverUrl || '', searchIndex, algoliaIndexBase)

    const location = useLocation()
    const searchKey = `${location.pathname}?${location.search}`

    const {
      selectedAvailabilityDayOffset,
      selectedStoreId
    } = useGlobalStore()

    const {
      searchPageSponsoredProducts,
      categoryPageSponsoredProducts,
      isRetailMediaEnabled
    } = useRetailMediaStore()

    const params = new URLSearchParams(search)
    const isCategory = category && category.name
    const pageType = isCategory ? 'category' : 'search'
    const parsedCategoryTree = categoryTree ? JSON.parse(JSON.stringify(categoryTree)) : null
    const categoryNames = createCategoryNameString(parsedCategoryTree?.map((category: { name: string }) => category.name) || [])
    const filterValue = isCategory ? categoryNames : params.get('q') || ''
    const categoryId = isCategory && category.id ? category.id : ''

    const availabilityOffset = selectedAvailabilityDayOffset || 1
    const storeId = selectedStoreId || '0'

    const maxSponsoredProducts = getMaxSponsoredProducts(pageType === 'search', searchPageSponsoredProducts, categoryPageSponsoredProducts)
    const hideSponsoredProductsForCategory = presentationAttributes?.hideSponsoredProducts

    const { productSearchWithSponsored } = useProductStore()

    const customSearchClient = useMemo(
      () => ({
          search(requests: any[]) {
            return productSearchWithSponsored({
              pageType,
              filterValue,
              selectedAvailabilityDayOffset: availabilityOffset,
              selectedStoreId: storeId,
              categoryId,
              hideSponsoredProductsForCategory
            }, requests)
          }
        }),
      [pageType, filterValue, availabilityOffset, storeId, categoryId, maxSponsoredProducts, isRetailMediaEnabled, hideSponsoredProductsForCategory]
    )

    return (
      <InstantSearchSSRProvider {...serverState}>
        {/** @TODO This is ignored from TS checking here because it requires an Algolia rewrite, which is in future plans. Remove the ignore when this happens
         * @ts-ignore */}
        <AlgoliaInstantSearch key={searchKey} searchClient={customSearchClient} indexName={searchIndex} routing={routing}>
          {children}
          <Stack spacing={['24px']} paddingBottom={'20px'}>
            <ResultsHeader
              category={category}
              content={contentAboveHeader}
              presentationAttributes={presentationAttributes}
              h2={h2}
              pageName={pageName}
            />
            <Box aria-hidden borderBottom="1px" borderColor="border0" />
            {category && (
              <Box display={['none', 'block']}>
                <RefinementsDisplay category={category} />
              </Box>
            )}
            <Results inGridPromos={inGridPromos} categoryTree={categoryTree} seo={seo} rowSlots={rowSlots} />
            <Pagination />
          </Stack>
        </AlgoliaInstantSearch>
      </InstantSearchSSRProvider>
    )
  },
)

export default InstantSearch

export function ResultsHeader({
  category,
  content,
  h2,
  presentationAttributes,
  pageName,
}: ResultsHeaderProps) {
  const search = useInstantSearch()
  const {results} = search || {}

  const { searchPageDeliveryKey, isSearchPage, pageType } = useSearchPageContent(results)

  // Fetch the topSlot and banner from the search page content
  const query = search.indexUiState.query || search.indexUiState.configure?.query

  let bannerContent = null
  const { topSlot } = usePLPBanners(searchPageDeliveryKey, pageType, query)

  if (content) {
    bannerContent = content
  }
  else if (!isServer) {
    bannerContent = topSlot
  }

  const navigate = useNavigation()

  // In case of an early submit, check for a valid redirect and navigate to it
  const redirectURL = isSearchPage ? getRedirectURL(results) : null
  if (redirectURL) {
    navigate(redirectURL)
  }

  return (
    <Flex direction={['column', 'row']} gap="16px">
      <Stack spacing="0px" flex="1" width="full">
        <Container
          top="0px"
          paddingX="0px"
          position="relative"
          display={category?.name ? 'none' : 'block'}
        >
          <Box paddingY="12px">
            <Breadcrumbs search={query} />
          </Box>
        </Container>
        <Box
          marginTop="8px"
          marginBottom={[
            content ? '8px' : '24px',
            content ? (h2?.heading2 ? '24px' : '48px') : '8px',
          ]}
          zIndex="docked"
        >
          <Text
            as="h1"
            variant="heading5"
            data-test-selector="results-header"
            color="accent0"
            lineHeight="taller"
            zIndex="header"
            textDecoration="none"
          >
            {pageName || category?.name || `Search results for '${query}'`}
          </Text>
          {h2 && (
            <Text as="h2" variant="text4" textAlign={h2?.textAlignment}>
              {h2?.heading2}
            </Text>
          )}
        </Box>
        {bannerContent}
        {!presentationAttributes?.hideSearchSuggestions && (
          <SearchSuggestions category={category} isContent={!!content}/>
        )}
        <Flex
          direction={['column-reverse', 'row']}
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex
            paddingTop={['16px', '0px']}
            marginTop={['16px', '0px']}
            alignSelf={['flex-start', 'center']}
            width={['full', 'auto']}
            borderTop={['1px', '0px']}
            borderColor="gray200"
          >
            <Stats />
          </Flex>
          <Filters category={category} />
        </Flex>
      </Stack>
    </Flex>
  )
}

export function Results(props: any) {
  const location = useLocation()
  const {sendViewSearch, sendViewCategory} = useEinstein()
  const globalStore = useGlobalStore()
  const {
    searchPageSponsoredProducts,
    categoryPageSponsoredProducts
  } = useRetailMediaStore()
  const search = useInstantSearch()
  const {results} = search

  // Fetch search content data
  const { searchPageDeliveryKey, isSearchPage, pageType } = useSearchPageContent(results)

  // Fetch Row Banners
  const { rowSlots } = usePLPBanners(searchPageDeliveryKey, pageType, results.query)

  const basketStore = useBasketStore()
  const [isInitialLoad, setInitialLoad] = useState(true)
  const sendViewItemListData = useViewItemListTracking()

  const maxSponsoredProducts = getMaxSponsoredProducts(isSearchPage, searchPageSponsoredProducts, categoryPageSponsoredProducts)

  const hitsItemsPrices = useItemsPrices(results.hits || [])
  const {currentCustomerGroups} = useCustomerStore()

  const structuredData = useMemo(() => {
    if (!location.pathname || !results.hits || !currentCustomerGroups) {
      return []
    }
    return [
      buildBreadcrumbsStructuredData(props?.categoryTree),
      buildProductListStructuredData(hitsItemsPrices)
    ]
  }, [location.pathname, results.hits, currentCustomerGroups])

  // Restores the browsing position when navigating back to the PLP from a PDP
  useEffect(() => {
    if (!isServer) {
      const scrollY = window.history.state?.scrollPositionY
      if (typeof scrollY === 'number') {
        setTimeout(() => {
          window.scrollTo(0, scrollY)
        }, 50)
        // Clear scrollPositionY after restoring browsing position
        const { scrollPositionY, ...rest } = window.history.state || {}
        window.history.replaceState(rest, '')
      }
    }
  }, [results.hits?.length, location.pathname])

  const cms = useContentStore()
  const debouncedSendViewEvents = useCallback(
    debounce(async (results: ReturnType<typeof useInstantSearch>['results']) => {
      const badges = await flowResult(cms.fetchBadgesForHits(results.hits))
      // Einstein view search data
      if (isSearchPage) {
        sendViewSearch({
          searchText: results.query,
          products: results.hits.map((hit) => ({id: hit.id})),
        })
        // GA search view item list data
        const viewItemListData = {
          items: transformHits(results.hits, globalStore.selectedPriceDayOffset, globalStore.selectedStoreId, globalStore.selectedRegion),
          badges: badges,
          listType: results.query,
          pageId: null,
          region: globalStore.selectedRegion
        }
        sendViewItemListData(viewItemListData)
      } else if (props?.categoryTree?.length) {
        const viewingCategory = props.categoryTree[props.categoryTree.length - 1]
        // Einstein view category data
        sendViewCategory({
          category: {
            id: viewingCategory.id,
          },
          products: results.hits.map((hit) => ({id: hit.id})),
          itemRange: results.page * results.hitsPerPage,
        })
        // GA category view item list data
        if (isInitialLoad || results.page > 0) {
          const viewItemListData = {
            items: transformHits(results.hits, globalStore.selectedPriceDayOffset, globalStore.selectedStoreId, globalStore.selectedRegion),
            badges: badges,
            listType: viewingCategory,
            pageId: null,
            region: globalStore.selectedRegion
          }
          sendViewItemListData(viewItemListData)
          setInitialLoad(false)
        }
      }
    }, 1000),
    [],
  )

  /** Einstein & GA view for PLP pages.
   * Tracks products that are displayed on page, respects pagination parameter change
   * */
  useEffect(() => {
    if (!globalStore.dataLoadedForAccuratePricingAndAvailabilty) return
    if (basketStore.status === 'init' || basketStore.status === 'pending') return
    debouncedSendViewEvents(results)
    return () => {
      debouncedSendViewEvents.cancel()
    }
  }, [results, globalStore.dataLoadedForAccuratePricingAndAvailabilty, basketStore.status])

  // Determine the canonical URL
  let categoryBaseUrl = ''
  if (props && props.categoryTree && props.categoryTree.length > 0) {
    categoryBaseUrl = props.categoryTree[props.categoryTree.length - 1].url
  } else if (typeof window !== 'undefined' && !window.location.pathname.includes('search')) {
    categoryBaseUrl = location.pathname.split('?')[0]
  }


  return results.hits.length <= 0 && results.processingTimeMS < 1 ? (
    <Box className={styles.hitsGrid}>
      <PLPSkeletonTiles />
    </Box>
  ) : (
    <>
      <Seo
        title={props?.seo?.pageTitle}
        description={props?.seo?.pageDescription}
        structuredData={structuredData as StructuredDataType}
        canonical={categoryBaseUrl}
      />
      <Hits
        inGridPromos={props.inGridPromos}
        categoryId={props?.categoryTree?.[props?.categoryTree?.length - 1]?.id ?? null}
        searchQuery={results?.query || null}
        rowSlots={rowSlots || props.rowSlots}
        maxSponsoredProducts={maxSponsoredProducts}
      />
    </>
  )
}

const SearchSuggestions = observer((props: SearchSuggestionsProps) => {
  const {category, isContent} = props
  const [orderedCategories, setorderedCategories] = useState<string[] | null>(null);
  const [hasSetOrdered, setHasSetOrdered] = useState(false)
  const {categories} = useCategoryStore()
  const sendSelectPromotion = useSelectPromotionTracking()
  const {routeMap} = useContentStore()
  const categoryData = category?.id ? categories[category.id] : null
  const {results} = useInstantSearch()
  const isMobileFlag = isMobile()
  const categoriesItems = useMemo(() => {
    return (
      categoryData?.categories?.map((categoryId) => {
        const [route] =
          Object.entries(routeMap).find(([route, dataStr]) => {
            const [_, _entityType, entityId] = dataStr.split('|')

            return entityId === categoryId
          }) || []

        return {
          path: route,
          value: categoryId,
          label: categories[categoryId].name || '',
          isRefined: false,
        }
      }) || []
    )
  }, [categoryData, orderedCategories, routeMap])

  const categoryStore = useCategoryStore()
  
  // Map over categories and set CategoryData.categories to correct order reflected in BM, change is picked up by existing logic above ^
  useEffect(() => {
    if (!category?.id) return;
    if (!categoriesItems || !categoryData || hasSetOrdered) return;

    categoryStore.getOrderedCategories(category?.id).then(categories => {
      setHasSetOrdered(true)
      const orderedCategories = Object.values(categories).map(obj => String(obj.id));
      categoryData.categories = orderedCategories
      setorderedCategories(categoryData.categories)
      
    }).catch(err => console.error(err))
  }, [categoriesItems])

  if (!categoriesItems.length) {
    return null
  }

  const availableSubcategoriesId = 
    results?.disjunctiveFacets?.find(obj => obj.name === 'categoryPageId') ||
    results?.facets?.find(obj => obj.name === 'categoryPageId')

  const filteredCategoriesItems = categoriesItems.filter(item => availableSubcategoriesId && availableSubcategoriesId?.data && availableSubcategoriesId?.data[item.value as keyof typeof availableSubcategoriesId.data])
  const promotions = filteredCategoriesItems.map((categoryItem, index) => {
    return {
      creative_name: category?.name,
      creative_slot: 'Sub Category links',
      promotion_id: categoryItem.path,
      promotion_name: categoryItem.label,
      link_position: index + 1
    }
  })

  if (!filteredCategoriesItems.length) {
    return null
  }

  // Only show the scrollbar on mobile view
  return (
    <Scrollbar showScrollbar={isMobileFlag}>
      <Flex 
        className={quickLinksContainer}
        data-test-selector="quick-links-container"
        marginTop={isContent ? '20px' : '0px'}
        >
        {filteredCategoriesItems.map((categoryItem, index) => (
            <BaseButton
              key={categoryItem.value}
              variant="unstyled"
              as={Link}
              to={categoryItem.path}
              extraClassName={searchSuggestionItem}
              onClickCapture={() => sendSelectPromotion(promotions[index])}
            >
              <Text fontSize="xs" weight="normal" variant="unstyled">
                {categoryItem.label}
              </Text>
            </BaseButton>
          ))}
        </Flex>
      </Scrollbar>
    )})
